/* eslint-disable no-use-before-define */
// @flow
import map from 'lodash/map';
import { PRODUCTS } from 'services/actions/productsActions';

import type { Action } from 'services/actions/Action.type';
import type { StateProducts, Variants, Favorites } from '../../sharedTypes';

export type ProductsState = $ReadOnly<{|
  displayedProductIds: Array<string>,
  sponsoredProductIds: Array<string>,
  allProductIds: Array<string>,
  products: StateProducts,
  variants: Variants,
  totalProductCount: number,
  allProductCount: number,
  fetchingProducts: boolean,
  productsRequestFailed: boolean,
  favorites: Favorites,
  shouldDisplaySponsoredPopupWarning: boolean,
  shouldDisplayRemoveGWPDisqualifyPopup: boolean,
  shouldDisplayRemoveGWPDowngradePopup: boolean,
  shouldDisplayRemoveGWPOutOfStockPopup: boolean,
  shouldDisplayRemoveGWPWarningPopup: boolean,
  variantToRemove: string | null,
  overrideDefaultVariantId: string | null,
  sortedProductIds: Array<string>,
  sortedAllProductIds: Array<string>,
  fetchingProductRecommendations: boolean,
  productRecommendationIds: Array<string>,
  ratings: any,
  reviewedProducts: any,
  subscribableProducts: any,
|}>;

export const productsInitialState = {
  displayedProductIds: [],
  sponsoredProductIds: [],
  allProductIds: [],
  products: {},
  variants: {},
  totalProductCount: 0,
  allProductCount: 0,
  fetchingProducts: true,
  productsRequestFailed: false,
  favorites: {},
  shouldDisplaySponsoredPopupWarning: false,
  shouldDisplayRemoveGWPDisqualifyPopup: false,
  shouldDisplayRemoveGWPDowngradePopup: false,
  shouldDisplayRemoveGWPOutOfStockPopup: false,
  shouldDisplayRemoveGWPWarningPopup: false,
  variantToRemove: null,
  overrideDefaultVariantId: null,
  sortedProductIds: [],
  sortedAllProductIds: [],
  fetchingSearchProducts: false,
  featuredProducts: {
    productIds: [],
    products: {},
    variants: {},
    error: false,
  },
  fetchingProductRecommendations: false,
  productRecommendationIds: [],
  ratings: {},
  reviewedProducts: {},
  subscribableProducts: {},
};

export function productsReducer(
  state: ProductsState = productsInitialState,
  action: Action
): ProductsState {
  switch (action.type) {
    case PRODUCTS.REQUEST_PRODUCTS: {
      const { offset, isAllProducts } = action.payload;
      const newDisplayedProductIds =
        offset === 0 && !isAllProducts ? [] : state.displayedProductIds;
      return {
        ...state,
        fetchingProducts: true,
        productsRequestFailed: false,
        displayedProductIds: newDisplayedProductIds,
      };
    }

    case PRODUCTS.REQUEST_BEST_SELLING_PRODUCTS:
    case PRODUCTS.REQUEST_FILTERED_PRODUCTS: {
      const { isAllProducts } = action.payload;
      const newDisplayedProductIds = isAllProducts
        ? state.displayedProductIds
        : [];
      return {
        ...state,
        fetchingProducts: true,
        productsRequestFailed: false,
        displayedProductIds: newDisplayedProductIds,
      };
    }

    case PRODUCTS.REQUEST_PRODUCTS_BY_ID: {
      return {
        ...state,
        fetchingProducts: true,
      };
    }

    case PRODUCTS.REQUEST_SEARCH_PRODUCTS: {
      return {
        ...state,
        fetchingSearchProducts: true,
      };
    }

    case PRODUCTS.FAILED_PRODUCTS_REQUEST: {
      return {
        ...state,
        productsRequestFailed: true,
        fetchingProducts: false,
      };
    }

    case PRODUCTS.RECEIVE_PRODUCTS: {
      const { products, variants, productIds, totalProductCount } =
        action.payload;

      return {
        ...state,
        ...addProductsAndVariants(
          state.products,
          products,
          state.variants,
          variants
        ),
        displayedProductIds: productIds,
        totalProductCount,
        fetchingProducts: false,
      };
    }

    case PRODUCTS.RECEIVE_BEST_SELLING_PRODUCTS:
    case PRODUCTS.RECEIVE_FILTERED_PRODUCTS: {
      const {
        products,
        variants,
        productIds,
        totalProductCount,
        sortedProductIds,
      } = action.payload;
      return {
        ...state,
        ...addProductsAndVariants(
          state.products,
          products,
          state.variants,
          variants
        ),
        displayedProductIds: productIds,
        totalProductCount,
        fetchingProducts: false,
        sortedProductIds,
      };
    }

    case PRODUCTS.RECEIVE_BEST_SELLING_ALL_PRODUCTS: {
      const {
        products,
        variants,
        productIds,
        totalProductCount,
        sortedProductIds,
      } = action.payload;
      return {
        ...state,
        ...addProductsAndVariants(
          state.products,
          products,
          state.variants,
          variants
        ),
        allProductIds: productIds,
        allProductCount: totalProductCount,
        fetchingProducts: false,
        sortedAllProductIds: sortedProductIds,
      };
    }

    case PRODUCTS.RECEIVE_PRODUCTS_RATINGS: {
      return {
        ...state,
        ratings: { ...state.ratings, ...action.payload },
      };
    }

    case PRODUCTS.RECEIVE_RATING_GRAPH: {
      return {
        ...state,
        productRatingGraph: { ...action.payload },
      };
    }

    case PRODUCTS.RECEIVE_HAS_REVIEWED: {
      const { productId, hasReviewed } = action.payload;
      return {
        ...state,
        reviewedProducts: {
          ...state.reviewedProducts,
          [productId]: hasReviewed,
        },
      };
    }

    case PRODUCTS.RECEIVE_FAVORITE_PRODUCTS: {
      const {
        products,
        variants,
        productIds,
        totalProductCount,
        sortedProductIds,
      } = action.payload;
      return {
        ...state,
        ...addProductsAndVariants(
          state.products,
          products,
          state.variants,
          variants
        ),
        displayedProductIds: productIds,
        totalProductCount,
        fetchingProducts: false,
        sortedProductIds,
      };
    }

    case PRODUCTS.RECEIVE_MORE_PRODUCTS: {
      const { products, variants, productIds } = action.payload;

      return {
        ...state,
        ...addProductsAndVariants(
          state.products,
          products,
          state.variants,
          variants
        ),
        displayedProductIds: [...state.displayedProductIds, ...productIds],
        fetchingProducts: false,
        fetchingSearchProducts: false,
      };
    }

    case PRODUCTS.RECEIVE_ALL_PRODUCTS: {
      const { products, variants, productIds, totalProductCount } =
        action.payload;

      return {
        ...state,
        ...addProductsAndVariants(
          state.products,
          products,
          state.variants,
          variants
        ),
        allProductIds: [...state.allProductIds, ...productIds],
        allProductCount: totalProductCount,
        fetchingProducts: false,
        fetchingSearchProducts: false,
      };
    }

    case PRODUCTS.RECEIVE_MORE_ALL_PRODUCTS: {
      const { products, variants, productIds } = action.payload;

      return {
        ...state,
        ...addProductsAndVariants(
          state.products,
          products,
          state.variants,
          variants
        ),
        allProductIds: [...state.allProductIds, ...productIds],
        fetchingProducts: false,
        fetchingSearchProducts: false,
      };
    }

    case PRODUCTS.RESET_ALL_PRODUCTS: {
      return {
        ...state,
        allProductIds: [],
        allProductCount: 0,
        sortedAllProductIds: [],
      };
    }

    case PRODUCTS.RECEIVE_SEARCH_PRODUCTS: {
      const { products, variants } = action.payload;
      return {
        ...state,
        ...addProductsAndVariants(
          state.products,
          products,
          state.variants,
          variants
        ),
        fetchingSearchProducts: false,
      };
    }

    case PRODUCTS.RECEIVE_SPONSORED_PRODUCTS: {
      const { products, variants, productIds } = action.payload;

      return {
        ...state,
        ...addProductsAndVariants(
          state.products,
          products,
          state.variants,
          variants
        ),
        sponsoredProductIds: productIds,
      };
    }

    case PRODUCTS.RECEIVE_CART_PRODUCTS: {
      const { products, variants } = action.payload;
      // we need to add cart products to the products store so that they display properly
      // even if they haven't been loaded on the sale page yet
      const { products: newProducts, variants: newVariants } =
        addProductsAndVariantsWithoutOverwritingProducts(
          state.products,
          products,
          state.variants,
          variants
        );

      return {
        ...state,
        products: newProducts,
        variants: newVariants,
      };
    }

    case PRODUCTS.SET_FAVORITES: {
      const favorites = {};
      action.favorites.forEach((productId) => {
        favorites[productId] = true;
      });
      return { ...state, favorites };
    }

    case PRODUCTS.RECEIVE_TOGGLED_FAVORITE: {
      const { productId, liked } = action.payload;
      return {
        ...state,
        favorites: {
          ...state.favorites,
          [productId]: liked,
        },
      };
    }

    case PRODUCTS.TOGGLE_SOLDOUT: {
      const { variantId, soldOut, isSubscribable } = action;
      const { variants, subscribableProducts } = state;
      if (isSubscribable) {
        return {
          ...state,
          subscribableProducts: {
            ...subscribableProducts,
            [variantId]: {
              ...subscribableProducts[variantId],
              soldOut,
            },
          },
        };
      }
      return {
        ...state,
        variants: {
          ...variants,
          [variantId]: {
            ...variants[variantId],
            soldOut,
          },
        },
      };
    }

    case PRODUCTS.DISPLAY_SPONSORED_POPUP_WARNING: {
      return {
        ...state,
        shouldDisplaySponsoredPopupWarning: true,
        shouldDisplayRemoveGWPDisqualifyPopup: false,
        shouldDisplayRemoveGWPDowngradePopup: false,
        shouldDisplayRemoveGWPOutOfStockPopup: false,
        shouldDisplayRemoveGWPWarningPopup: false,
        variantToRemove: action.variantId,
      };
    }

    case PRODUCTS.DISPLAY_REMOVE_GWP_DISQUALIFY_POPUP: {
      return {
        ...state,
        shouldDisplaySponsoredPopupWarning: false,
        shouldDisplayRemoveGWPDisqualifyPopup: true,
        shouldDisplayRemoveGWPDowngradePopup: false,
        shouldDisplayRemoveGWPOutOfStockPopup: false,
        shouldDisplayRemoveGWPWarningPopup: false,
        variantToRemove: action.variantId,
      };
    }

    case PRODUCTS.DISPLAY_REMOVE_GWP_DOWNGRADE_POPUP: {
      return {
        ...state,
        shouldDisplaySponsoredPopupWarning: false,
        shouldDisplayRemoveGWPDisqualifyPopup: false,
        shouldDisplayRemoveGWPDowngradePopup: true,
        shouldDisplayRemoveGWPOutOfStockPopup: false,
        shouldDisplayRemoveGWPWarningPopup: false,
        variantToRemove: action.variantId,
      };
    }

    case PRODUCTS.DISPLAY_REMOVE_GWP_OUT_OF_STOCK_POPUP: {
      return {
        ...state,
        shouldDisplaySponsoredPopupWarning: false,
        shouldDisplayRemoveGWPDisqualifyPopup: false,
        shouldDisplayRemoveGWPDowngradePopup: false,
        shouldDisplayRemoveGWPOutOfStockPopup: true,
        shouldDisplayRemoveGWPWarningPopup: false,
        variantToRemove: action.variantId,
      };
    }

    case PRODUCTS.DISPLAY_REMOVE_GWP_WARNING_POPUP: {
      return {
        ...state,
        shouldDisplaySponsoredPopupWarning: false,
        shouldDisplayRemoveGWPDisqualifyPopup: false,
        shouldDisplayRemoveGWPDowngradePopup: false,
        shouldDisplayRemoveGWPOutOfStockPopup: false,
        shouldDisplayRemoveGWPWarningPopup: true,
        variantToRemove: action.variantId,
      };
    }

    case PRODUCTS.HIDE_SPONSORED_WARNING: {
      return {
        ...state,
        shouldDisplaySponsoredPopupWarning: false,
        shouldDisplayRemoveGWPDisqualifyPopup: false,
        shouldDisplayRemoveGWPDowngradePopup: false,
        shouldDisplayRemoveGWPOutOfStockPopup: false,
        shouldDisplayRemoveGWPWarningPopup: false,
        variantToRemove: null,
      };
    }

    case PRODUCTS.SET_OVERRIDE_DEFAULT_VARIANT_ID: {
      return {
        ...state,
        overrideDefaultVariantId: action.variantId,
      };
    }

    case PRODUCTS.RECEIVE_FEATURED_PRODUCTS: {
      const { products, variants } = action.payload;
      return {
        ...state,
        ...addProductsAndVariants(
          state.products,
          products,
          state.variants,
          variants
        ),
        featuredProducts: action.payload,
      };
    }

    case PRODUCTS.RECEIVE_FEATURED_PRODUCTS_ERROR: {
      return {
        ...state,
        featuredProducts: { ...state.featuredProducts, error: true },
      };
    }

    case PRODUCTS.REQUEST_PRODUCT_RECOMMENDATIONS: {
      return {
        ...state,
        productRecommendationIds: [],
        fetchingProductRecommendations: true,
      };
    }

    case PRODUCTS.RECEIVE_PRODUCT_RECOMMENDATIONS: {
      const { products, variants, productIds } = action.payload;

      return {
        ...state,
        ...addProductsAndVariants(
          state.products,
          products,
          state.variants,
          variants
        ),
        productRecommendationIds: productIds,
        fetchingProductRecommendations: false,
      };
    }

    case PRODUCTS.RECEIVE_PRODUCT_RECOMMENDATIONS_ERROR: {
      return {
        ...state,
        fetchingProductRecommendations: false,
      };
    }

    case PRODUCTS.RECEIVE_SUBSCRIBABLE_PRODUCTS: {
      return {
        ...state,
        subscribableProducts: bundleSubscribableProducts(action.payload),
      };
    }

    default: {
      return state;
    }
  }
}

function addNewVariants(addedVariants, prevVariants) {
  const newVariants = { ...prevVariants };
  for (const variantId in addedVariants) {
    // below is a temporary fix for parsing descriptors
    // the root of the wrong format in descriptors comes from shop table
    // aleady we have some data in db box which have wrong fomat, below lins is goign to fix that
    const newDescriptors = addedVariants[variantId].variantDescriptors.map(
      (variantDescriptor) => {
        return {
          ...variantDescriptor,
          descriptor: variantDescriptor.descriptor
            .replace(/&#039;/g, "'")
            .replace(/&amp;/g, '&')
            .replace(/&quot;/g, '"'),
        };
      }
    );

    newVariants[variantId] = {
      ...addedVariants[variantId],
      variantDescriptors: newDescriptors,
      isSoldOutOnFetch: addedVariants[variantId].soldOut,
    };
  }
  return newVariants;
}

function addProductsAndVariants(
  prevProducts: StateProducts,
  addedProducts: StateProducts,
  prevVariants: Variants,
  addedVariants: Variants
) {
  // Unfortunately, flow doesn't handle copying objects with spread
  // when the second spread object has an indexer. Hopefully they'll
  // fix this soon. See https://medium.com/flow-type/spreads-common-errors-fixes-9701012e9d58
  const newProducts = { ...prevProducts };
  for (const productId in addedProducts) {
    newProducts[productId] = addedProducts[productId];
  }

  const newVariants = addNewVariants(addedVariants, prevVariants);
  return { products: newProducts, variants: newVariants };
}

function addProductsAndVariantsWithoutOverwritingProducts(
  prevProducts: StateProducts,
  addedProducts: StateProducts,
  prevVariants: Variants,
  addedVariants: Variants
) {
  // Unfortunately, flow doesn't handle copying objects with spread
  // when the second spread object has an indexer. Hopefully they'll
  // fix this soon. See https://medium.com/flow-type/spreads-common-errors-fixes-9701012e9d58
  const newProducts = { ...prevProducts };
  for (const productId in addedProducts) {
    if (!newProducts.hasOwnProperty(productId)) {
      newProducts[productId] = addedProducts[productId];
    }
  }
  const newVariants = addNewVariants(addedVariants, prevVariants);
  return { products: newProducts, variants: newVariants };
}

const bundleSubscribableProducts = (subscribableProducts) => {
  const bundledProduct = { ...subscribableProducts };
  const variantIds = Object.keys(subscribableProducts);
  variantIds.forEach((variantId) => {
    bundledProduct[variantId] = {
      ...bundledProduct[variantId],
      isSoldOutOnFetch: bundledProduct[variantId].soldOut,
    };
  });
  return bundledProduct;
};
